import Loader from '@/components/Loader';
import ToggleCard from '@/components/ui/ToggleCard';
import TagTypes from '@/constants/TagTypes';
import useTags from '@/hooks/useTags';
import useUserTags from '@/hooks/useUserTags';
import {CheckCircleIcon} from '@heroicons/react/24/solid';
import {useUser} from '@lore/auth';
import {useQueryMutation} from '@lore/query-connect';
import {PayloadStates} from '@lore/utils';
import {useQueryClient} from '@tanstack/react-query';
import _ from 'lodash';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import EditableGrades from './EditableGrades';
import EditableSkills from './EditableSkills';
import EditableSubjects from './EditableSubjects';
import {useTranslation} from 'react-i18next';
import { marked } from 'marked';

export default function YourInvolvementSection() {
  const {t} = useTranslation();
  const currentUser = useUser();
  const intents = useTags(TagTypes.Intent);
  const userIntents = useUserTags(currentUser, TagTypes.Intent);
  const queryClient = useQueryClient();

  const mutationCreate = useQueryMutation('tagNode.create');
  const mutationDestroy = useQueryMutation('tagNode.destroy2');

  const currentUserEducatorIntent = useMemo(() => _.find(userIntents.data, (ui) => ui.data.name === 'Educator'), [userIntents.data]);
  const currentUserPartnerIntent = useMemo(() => _.find(userIntents.data, (ui) => ui.data.name === 'Partner'), [userIntents.data]);

  const [isEducator, setIsEducator] = useState(!!currentUserEducatorIntent);
  const [isPartner, setIsPartner] = useState(!!currentUserPartnerIntent);
  const [isChanging, setIsChanging] = useState(false);

  useEffect(() => {
    if (userIntents.state === PayloadStates.FETCHING
      || isChanging
      || mutationCreate.isLoading
      || mutationDestroy.isLoading) {
      return;
    }
    setIsEducator(!!currentUserEducatorIntent);
  }, [currentUserEducatorIntent, mutationCreate.isLoading, mutationDestroy.isLoading]);

  useEffect(() => {
    if (userIntents.state === PayloadStates.FETCHING
      || isChanging
      || mutationCreate.isLoading
      || mutationDestroy.isLoading) {
      return;
    }
    setIsPartner(!!currentUserPartnerIntent);
  }, [currentUserPartnerIntent, mutationCreate.isLoading, mutationDestroy.isLoading]);

  const onChangeEducator = useCallback(async (checked) => {
    try {
      setIsChanging(true);
      setIsEducator(checked);
      const tag = _.find(intents.data, (t) => t.data.name === 'Educator');

      if (checked && !currentUserEducatorIntent && tag) {
        await mutationCreate.mutateAsync({
          tagId: tag.id,
          userId: currentUser.id
        });
        await userIntents._invalidateQuery();
        queryClient.invalidateQueries(['currentUser']).then(/* do not need to wait */);
      } else if (!checked && currentUserEducatorIntent) {
        await mutationDestroy.mutateAsync(currentUserEducatorIntent._tagNode);
        await userIntents._invalidateQuery();
        queryClient.invalidateQueries(['currentUser']).then(/* do not need to wait */);;
      }
    } finally {
      setIsChanging(false);
    }
  }, [currentUser.id, mutationCreate, mutationDestroy, intents.data, currentUserEducatorIntent, userIntents]);

  const onChangePartner = useCallback(async (checked) => {
    try {
      setIsChanging(true);
      setIsPartner(checked);
      const tag = _.find(intents.data, (t) => t.data.name === 'Partner');

      if (checked && !currentUserPartnerIntent && tag) {
        await mutationCreate.mutateAsync({
          tagId: tag.id,
          userId: currentUser.id
        });
        await userIntents._invalidateQuery();
        queryClient.invalidateQueries(['currentUser']).then(/* do not need to wait */);
      } else if (!checked && currentUserPartnerIntent) {
        await mutationDestroy.mutateAsync(currentUserPartnerIntent._tagNode);
        await userIntents._invalidateQuery();
        queryClient.invalidateQueries(['currentUser']).then(/* do not need to wait */);
      }
    } finally {
      setIsChanging(false);
    }
  }, [currentUser.id, mutationCreate, mutationDestroy, intents.data, currentUserPartnerIntent, userIntents]);

  if (
    intents.state === PayloadStates.FETCHING ||
    userIntents.state === PayloadStates.FETCHING
  ) {
    return <Loader/>;
  }

  return (
    <section id="involvement" className="py-8">
      <h2 className="mb-8 text-2xl font-semibold text-cs-gray-900">Your involvement</h2>

      <div className="space-y-8">
        <div className="grid items-start gap-8 lg:grid-cols-[280px_1fr]">
          <ToggleCard
            label="Planning projects and events for the learners I teach"
            checked={isEducator}
            onChange={onChangeEducator}
          />
          <div className="space-y-6">
            <div>
              <h4 className="mb-2 text-base font-semibold text-cs-gray-900">What&apos;s this about?</h4>
              <p className="text-base markdown-content"
                 dangerouslySetInnerHTML={{ __html: marked.parse(t('account.educator.description')) }}>
              </p>
            </div>

            <div
              className={`grid transition-all duration-300 ease-in-out ${isEducator ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
              <div className="overflow-hidden space-y-6">
                <EditableSubjects/>
                <EditableGrades/>

                <div
                  className="flex items-center gap-2 rounded-2xl border border-cs-green-500 bg-green-100 px-4 py-4">
                  <CheckCircleIcon className="size-5 shrink-0 text-cs-green-500"/>
                  <p className="text-base font-semibold text-cs-gray-900">
                    You will be matched with opportunities as an educator
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <hr className="border-gray-200"/>

        <div className="grid items-start gap-8 lg:grid-cols-[280px_1fr]">
          <ToggleCard
            label="Volunteering to share my skills with learners and educators"
            checked={isPartner}
            onChange={onChangePartner}
          />
          <div className="space-y-6">
            <div>
              {/*<pre>{JSON.stringify(currentUserPartnerIntent, null, 2)}</pre>*/}
              <h4 className="mb-2 text-base font-semibold text-cs-gray-900">What&apos;s this about?</h4>
              <p className="text-base markdown-content"
                 dangerouslySetInnerHTML={{ __html: marked.parse(t('account.partner.description')) }}>
              </p>
            </div>
            <div
              className={`grid transition-all duration-300 ease-in-out ${isPartner ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
              <div className="overflow-hidden space-y-6">
                <EditableSkills/>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
